/** Entonnoir de prévention partagé par les évaluations PHY et RPS. */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck } from "@fortawesome/free-solid-svg-icons"
import { PhysicalRiskScaleItem } from "@/entities/physical-risk/model/physical-risk.types"
import { getPreventionColorAt } from "@/entities/physical-risk/ui/physical-risk-scale-colors.utils"

//Geometrie du triangle : tout se deduit de ces valeurs. Deux lignes sont reservees a la precision
//pour que les descriptions longues restent lisibles sans etre tronquees trop tot.
const SLICE_HEIGHT = 60
const ROW_GAP = 8 //Respiration entre deux tranches
const TRIANGLE_TOP_WIDTH = 150 //Largeur de l'entree, en haut du niveau le plus eleve
const TRIANGLE_BOTTOM_WIDTH = 45 //Largeur du bec, en bas du niveau le plus faible
const LABEL_GAP = 20
const VALUE_FONT_SIZE = 18 //Meme corps que le chiffre d'une echelle classique
const VALUE_PADDING_LEFT = 14
const INACTIVE_COLOR = "#E2E8F0" //Niveau que la prevention n'atteint pas

interface Props {
  scale: readonly PhysicalRiskScaleItem[]
  value: number | null
  showValues?: boolean
  onChange?: (value: number) => void
}

export default function PreventionFunnel({ scale, value, showValues = false, onChange }: Props) {
  if (!scale.length) return null
  const scaleLength = scale.length
  const maxScaleValue = scale[scaleLength - 1]?.value ?? 1
  const activeValue = value === null ? null : Math.min(value, maxScaleValue)
  const activeIndex = scale.findIndex((scaleItem) => scaleItem.value === activeValue)
  const isEditable = Boolean(onChange)

  //Les ecarts entre tranches comptent dans la hauteur totale : une tranche reprend donc quelques
  //pixels plus etroite que le bas de la precedente, et le bord droit se lit comme un trait continu
  const funnelHeight = scaleLength * SLICE_HEIGHT + (scaleLength - 1) * ROW_GAP

  /** Largeur du triangle a une hauteur donnee : il se resserre regulierement vers le bas */
  function getTriangleWidthAt(y: number) {
    return TRIANGLE_TOP_WIDTH + ((TRIANGLE_BOTTOM_WIDTH - TRIANGLE_TOP_WIDTH) * y) / funnelHeight
  }

  /** Une ligne par niveau, du plus eleve (en haut) au plus faible (en bas) */
  function renderFunnelRow(scaleItem: PhysicalRiskScaleItem, scaleIndex: number, rowIndex: number) {
    const isActive = scaleItem.value === activeValue
    //Remplissage cumulatif : le niveau retenu et tous ceux qu'il contient
    const isFilled = activeIndex !== -1 && scaleIndex <= activeIndex
    const color = getPreventionColorAt(scaleIndex / Math.max(1, scaleLength - 1))

    //Bord gauche droit, bord droit en biais : les tranches empilees dessinent l'entonnoir
    const top = rowIndex * (SLICE_HEIGHT + ROW_GAP)
    const topWidth = getTriangleWidthAt(top)
    const bottomWidth = getTriangleWidthAt(top + SLICE_HEIGHT)
    const points = [
      [0, 0],
      [topWidth, 0],
      [bottomWidth, SLICE_HEIGHT],
      [0, SLICE_HEIGHT]
    ]

    return (
      <button
        key={scaleItem.value}
        type="button"
        className={`${isEditable ? "_hover" : ""} flex w-full items-center bg-transparent p-0 text-left`}
        style={{ border: "none", height: SLICE_HEIGHT }}
        aria-pressed={isActive}
        onClick={() => onChange?.(scaleItem.value)}>
        <svg
          className="block shrink-0"
          height={SLICE_HEIGHT}
          width={TRIANGLE_TOP_WIDTH}
          style={{ marginRight: LABEL_GAP }}
          viewBox={`0 0 ${TRIANGLE_TOP_WIDTH} ${SLICE_HEIGHT}`}>
          <polygon fill={isFilled ? color : INACTIVE_COLOR} points={points.map(([x, y]) => `${x},${y}`).join(" ")} />
          {showValues && (
            <text
              dominantBaseline="middle"
              fill="white"
              fontSize={VALUE_FONT_SIZE}
              x={VALUE_PADDING_LEFT}
              y={SLICE_HEIGHT / 2}>
              {scaleItem.value}
            </text>
          )}
        </svg>
        {/* Coche placee comme sur les echelles classiques : devant le libelle et sa precision */}
        {isActive && <FontAwesomeIcon className="mr-10 shrink-0 text-base" icon={faCheck} style={{ color }} />}
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <div className={isActive ? "font-bold" : undefined}>{scaleItem.label}</div>
          {/* Les echelles enregistrees avant l'ajout du champ n'ont pas de precision. */}
          {scaleItem.description && (
            <div className="grey-t line-clamp-2 text-sm leading-snug">
              {scaleItem.description}
            </div>
          )}
        </div>
      </button>
    )
  }

  //Du niveau le plus eleve au plus faible : le triangle se lit de haut en bas
  const rows = scale.map((scaleItem, scaleIndex) => ({ scaleIndex, scaleItem })).reverse()

  return (
    <div className="flex flex-col" style={{ gap: ROW_GAP }}>
      {rows.map(({ scaleIndex, scaleItem }, rowIndex) => renderFunnelRow(scaleItem, scaleIndex, rowIndex))}
    </div>
  )
}
